import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Container, ConfirmDialog } from '@beyond-silence/ui';
import { useArticles, deleteArticle } from '../hooks/useSupabaseData';
import type { Database } from '@beyond-silence/types';

type ArticleStatus = Database['public']['Tables']['articles']['Row']['status'];

const STATUS_TABS: { key: ArticleStatus | 'all'; i18nKey: string }[] = [
  { key: 'all', i18nKey: 'all' },
  { key: 'draft', i18nKey: 'drafts' },
  { key: 'in_review', i18nKey: 'inReview' },
  { key: 'published', i18nKey: 'published' },
  { key: 'archived', i18nKey: 'archived' },
];

const STATUS_BADGES: Record<ArticleStatus, string> = {
  draft:      'bg-soft/60 text-ink-light',
  in_review:  'bg-highlight/40 text-ink',
  published:  'bg-green-100 text-green-800',
  archived:   'bg-gray-200 text-gray-600',
};

/**
 * Staff article list — responsive table that collapses to cards on mobile.
 * Fetches from Supabase, filtered by article status.
 */
export function ArticlesPage() {
  const { t } = useTranslation('staff');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ArticleStatus | 'all'>('all');

  const { articles, loading, error, refetch } = useArticles({
    status: activeTab === 'all' ? undefined : activeTab,
  });

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; headline: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteArticle(deleteTarget.id);
      setDeleteTarget(null);
      refetch();
    } catch {
      // silently handle — refetch will show stale state if RLS blocked it
    }
    setDeleting(false);
  };

  return (
    <Container size="dashboard" className="py-4 sm:py-6 md:py-8">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-ui text-ui-lg font-bold text-ink">{t('articles.title')}</h1>
        <button
          onClick={() => navigate('/articles/new')}
          className="inline-flex items-center gap-2 self-start rounded-pill bg-primary px-4 py-2 font-ui text-ui-sm font-medium text-white transition-colors hover:bg-primary-600 sm:self-auto"
        >
          + {t('articles.newArticle')}
        </button>
      </div>

      {/* Filter tabs */}
      <div className="mt-4 flex gap-1 overflow-x-auto pb-1 sm:mt-5 md:mt-6">
        {STATUS_TABS.map(({ key, i18nKey }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`whitespace-nowrap rounded-pill px-3 py-1.5 font-ui text-ui-xs font-medium transition-colors
              ${activeTab === key
                ? 'bg-primary text-white'
                : 'text-ink-light hover:bg-soft/40'
              }
            `.trim().replace(/\s+/g, ' ')}
          >
            {i18nKey === 'all' ? t('categories.all', { ns: 'public' }) : t(`articles.${i18nKey}`)}
          </button>
        ))}
      </div>

      {/* Error state */}
      {error && (
        <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-4 text-center">
          <p className="font-ui text-ui-sm text-primary">{error}</p>
          <button
            onClick={refetch}
            className="mt-2 rounded-pill border border-primary/30 px-3 py-1 font-ui text-ui-xs text-primary hover:bg-primary/10"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="mt-6 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="animate-skeleton rounded-md bg-soft/40 h-14" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && articles.length === 0 && (
        <div className="mt-6 rounded-lg border-2 border-dashed border-soft p-8 text-center md:mt-8 md:p-12">
          <p className="font-ui text-ui-base text-ink-light">{t('articles.noArticles')}</p>
          <button
            onClick={() => navigate('/articles/new')}
            className="mt-4 inline-flex items-center gap-2 rounded-pill bg-primary px-4 py-2 font-ui text-ui-sm font-medium text-white hover:bg-primary-600"
          >
            + {t('articles.newArticle')}
          </button>
        </div>
      )}

      {/* Articles table */}
      {!loading && !error && articles.length > 0 && (
        <div className="table-scroll mt-4 sm:mt-5 md:mt-6">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('articles.title')}</th>
                <th>{t('articles.status')}</th>
                <th>{t('articles.language')}</th>
                <th>Views</th>
                <th>{t('articles.updated')}</th>
                <th className="w-24">{t('articles.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((article) => {
                const firstTranslation = article.translations?.[0];
                return (
                  <tr key={article.id} className="transition-colors hover:bg-soft/20">
                    <td data-label={t('articles.title')}>
                      <span
                        className="font-medium cursor-pointer hover:text-primary"
                        onClick={() => navigate(`/articles/${article.id}/edit`)}
                      >
                        {firstTranslation?.headline ?? article.slug}
                      </span>
                    </td>
                    <td data-label={t('articles.status')}>
                      <span className={`inline-block rounded-pill px-2 py-0.5 font-ui text-ui-xs font-medium ${STATUS_BADGES[article.status]}`}>
                        {t(`articles.${article.status === 'in_review' ? 'inReview' : article.status === 'draft' ? 'drafts' : article.status}`)}
                      </span>
                    </td>
                    <td data-label={t('articles.language')}>
                      {article.translations?.map((tr) => tr.language.toUpperCase()).join(', ') || '—'}
                    </td>
                    <td data-label="Views" className="font-ui text-ui-sm text-ink-light tabular-nums">
                      {(article as any).total_views?.toLocaleString() ?? '—'}
                    </td>
                    <td data-label={t('articles.updated')}>
                      {new Date(article.updated_at).toLocaleDateString()}
                    </td>
                    <td data-label={t('articles.actions')}>
                      <div className="flex items-center gap-2">
                        {/* Edit */}
                        <button
                          onClick={() => navigate(`/articles/${article.id}/edit`)}
                          className="rounded-md p-1.5 text-ink-light hover:bg-soft/40 hover:text-secondary transition-colors"
                          title="Edit"
                          aria-label="Edit article"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                          </svg>
                        </button>
                        {/* Delete */}
                        <button
                          onClick={() => setDeleteTarget({
                            id: article.id,
                            headline: firstTranslation?.headline ?? article.slug,
                          })}
                          className="rounded-md p-1.5 text-ink-light hover:bg-primary/10 hover:text-primary transition-colors"
                          title="Delete"
                          aria-label="Delete article"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete article"
        message={`Are you sure you want to delete "${deleteTarget?.headline}"? This action cannot be undone. All translations, media, and analytics for this article will be permanently removed.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        danger
      />
    </Container>
  );
}
